import {
  memo,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, Medal } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { TournamentLeaderboardItem } from "@/hooks/tournaments/use-tournament-leaderboard";
import { SectionLabel } from "@/components/campaigns/campaign-shell/campaign-sections";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { useTournamentLeaderboard } from "@/hooks/tournaments/use-tournament-leaderboard";
import { PrizeText } from "@/components/prize-text";
import { Badge } from "@/components/ui/badge";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";

// The podium's treatment, all tokens: the medal icon in the metal's flat colour, rank and name
// in the metal's gradient with a soft glow shifted toward that metal's contrast hue (see the
// `--glow-*` tokens). Gold is the prize gold, so rank 1 reads as "the prize"; silver and bronze
// are their own metals, never brand colours — the reader's own row owns the brand.
const PODIUM: Record<
  number,
  {
    icon: string;
    type: string;
    glow: string;
    bg: string;
    /** The place badge's colour on the prize table. */
    badge: "prizes" | "silver" | "bronze";
  }
> = {
  1: {
    badge: "prizes",
    icon: "text-prizes",
    type: "text-prize-gold",
    glow: "glow-prizes",
    bg: "bg-leaderboard-podium-gold",
  },
  2: {
    badge: "silver",
    icon: "text-medal-silver",
    type: "text-medal-silver-gradient",
    glow: "glow-silver",
    bg: "bg-leaderboard-podium-silver",
  },
  3: {
    badge: "bronze",
    icon: "text-medal-bronze",
    type: "text-medal-bronze-gradient",
    glow: "glow-bronze",
    bg: "bg-leaderboard-podium-bronze",
  },
};

/** Greyed placeholder rows shown in the desktop table when there are no
 *  participants yet — mirrors the top-10 it would otherwise show. */
const EMPTY_ROW_COUNT = 10;

export type LeaderboardRowSize = "default" | "compact";

/**
 * Column template shared by every board (desktop table, mobile summary, dialog). The rows are
 * `subgrid` children, so every column — and above all the prize — is as wide as its widest cell
 * on the board: headers sit over their column, scores line up, the prize never truncates, and
 * the player name is what gives way.
 */
// Priority when the board is narrow: the score never shrinks, the player name gives way down to a
// floor so it never vanishes, and the prize takes what is left — a prize wider than that stays on
// one line and glides (`PrizeText fit="marquee"`, whose `min-w-0` lets the auto track shrink below
// the text instead of pushing the board wider than its card).
export const LEADERBOARD_GRID =
  "grid grid-cols-[auto_minmax(6rem,1fr)_auto_auto_auto]";
/** The compact lists (mobile summary, dialog) hide the player name — rank, score and prize get
 *  the width — so the name track is mostly the spacer that pushes the figures to the right. Its
 *  3rem floor is what lets the one name that IS shown, the reader's "You", fit: without it the
 *  auto-sized prize column takes every spare pixel and the name column collapses to nothing. */
export const LEADERBOARD_GRID_COMPACT =
  "grid grid-cols-[auto_minmax(3rem,1fr)_auto_auto_auto]";

// Column widths per size. Rank/score/dot/prize are fixed so the columns line up
// across rows; the player name is the flexible column and truncates first. With
// `prizeFit="auto"` (mobile summary, no headers) the prize instead sizes to its
// content so it isn't truncated — the player name gives way instead.
const SIZE: Record<
  LeaderboardRowSize,
  {
    gap: string;
    pad: string;
    /** Row type: 14px on the desktop table, 13px on the compact boards (phones). */
    type: string;
    /** The prize on the PRIZE board (rank + prize only, so it has the row's width to itself):
     *  a step up from the row type, where the live leaderboard keeps the prize dense. */
    prizeType: string;
    /** Podium medal glyph, in step with the type. */
    medal: string;
    rank: string;
    score: string;
    dot: string;
    prize: string;
    prizeMin: string;
  }
> = {
  default: {
    gap: "gap-2.5",
    pad: "px-4 py-2.5",
    type: "text-[14px] leading-5",
    prizeType: "text-[15px] leading-5",
    medal: "size-[18px]",
    rank: "w-[46px]",
    score: "w-20",
    dot: "w-[18px]",
    prize: "w-24",
    prizeMin: "min-w-24",
  },
  compact: {
    gap: "gap-2",
    pad: "px-3 py-2 lg:py-2.5",
    type: "text-[11px] leading-4 lg:text-[13px] lg:leading-5",
    prizeType: "text-[13px] leading-[18px] lg:text-[14px] lg:leading-5",
    medal: "size-3.5 lg:size-4",
    rank: "w-8",
    score: "w-[60px]",
    dot: "w-2.5",
    prize: "w-[88px]",
    prizeMin: "min-w-[88px]",
  },
};

const LeaderboardRowImpl = ({
  item,
  scoreSuffix,
  isYou = false,
  size = "default",
  prizeFit = "fixed",
  layout = "flex",
  nameVisible = true,
  className,
  ref,
}: {
  item: TournamentLeaderboardItem;
  scoreSuffix: string;
  isYou?: boolean;
  ref?: Ref<HTMLDivElement>;
  /** `false` on the compact lists: the name stays for screen readers only, and its column
   *  becomes the space between rank and figures (the reader's row is told apart by its tint). */
  nameVisible?: boolean;
  size?: LeaderboardRowSize;
  /** "fixed": prize sits in a fixed column (shrinks, then glides). "auto": prize
   *  sizes to its content — use where columns needn't line up. */
  prizeFit?: "fixed" | "auto";
  /** "grid": the row is a `subgrid` child of a `LEADERBOARD_GRID` parent, sharing its columns
   *  with every other row (compact lists). "flex": self-contained row with fixed column widths
   *  (the desktop table, whose header uses the same widths). */
  layout?: "flex" | "grid";
  className?: string;
}) => {
  const { position, score, name, isPlayer, prize, isEmptySlot } = item;
  const you = isYou || isPlayer;
  const s = SIZE[size];
  const podium = !isEmptySlot ? PODIUM[position] : undefined;
  const grid = layout === "grid";
  return (
    // Table semantics on the row and its cells: the board is a CSS grid of divs, so assistive
    // tech gets the rank / player / score / prize structure from the roles, not the layout.
    <div
      ref={ref}
      role="row"
      className={cn(
        grid
          ? "col-span-full grid grid-cols-subgrid items-center"
          : "flex w-full items-center",
        s.gap,
        s.pad,
        // The reader's row is unmistakable (brand tint that breathes, hairlines); the podium
        // rows get a fainter metal tint so they lift off the field without reading as "you".
        you
          ? "bg-leaderboard-you"
          : podium
            ? podium.bg
            : "bg-background-card-surface",
        className
      )}
    >
      <div
        role="cell"
        className={cn("flex shrink-0 items-center gap-1.5", !grid && s.rank)}
      >
        {podium && (
          <Medal
            className={cn("shrink-0", s.medal, podium.icon)}
            strokeWidth={1.5}
          />
        )}
        {/* Hierarchy: the podium's rank glows in its metal, the reader's own rank in the brand,
            everyone else's is quiet; the name carries the row, the prize is the gold accent. */}
        <span
          className={cn(
            "font-heading",
            s.type,
            podium
              ? cn("font-bold", podium.type, podium.glow)
              : you
                ? "font-bold text-active"
                : "font-normal text-muted-foreground"
          )}
        >
          {position}
        </span>
      </div>
      <span
        role="cell"
        className={cn(
          "min-w-0 flex-1 truncate font-heading",
          s.type,
          you
            ? "glow-active font-bold text-active"
            : podium
              ? cn("font-semibold", podium.type, podium.glow)
              : "font-medium text-foreground-secondary"
        )}
      >
        {nameVisible ? name : <span className="sr-only">{name}</span>}
      </span>
      {/* The score as a text badge (the same treatment as the "+10 pts" points): the text colour
          on a faint ramp with a soft shadow, the same on every row — the name and the prize carry
          the hierarchy — except the reader's own score, which keeps the progress token like the
          rest of their row. `py-0` keeps the row at its 40px. */}
      <Badge
        role="cell"
        variant="text"
        color={you ? "active" : "foreground"}
        className={cn(
          "justify-end py-0 text-right font-heading",
          s.type,
          // No `truncate` in the grid: an overflow-hidden grid item may shrink below its text,
          // and a score must never be cut.
          grid ? "whitespace-nowrap" : "truncate",
          you ? "font-semibold" : "font-normal",
          !grid && s.score
        )}
      >
        {isEmptySlot ? "—" : `${score.toLocaleString()}${scoreSuffix}`}
      </Badge>
      <span
        className={cn(
          "shrink-0 text-center font-heading text-border",
          s.type,
          !grid && s.dot
        )}
        aria-hidden
      >
        ·
      </span>

      {/* The prize signature at row size — the gold gradient without the emboss on the rows that
          matter, a quieter flat gold on the field. One line always: in the grid the shared column
          is as wide as the widest prize up to the room left after rank, name floor and score; in
          flex mode auto grows from the column's baseline width to fit the full prize (capped at
          60%), fixed is a set column. A prize still too wide shrinks, then glides
          (`fit="marquee"`) — it never wraps and never truncates. In every case the player name is
          what gives way first. */}
      <PrizeText
        role="cell"
        title={typeof prize === "string" ? prize : undefined}
        size="inherit"
        weight={podium || you ? "semibold" : "medium"}
        tone={podium || you ? "gold" : "flat"}
        shadow={false}
        fit="marquee"
        className={cn(
          "shrink-0 text-right font-heading",
          s.type,
          !(podium || you) && "text-prizes/75",
          grid
            ? undefined
            : prizeFit === "auto"
              ? cn(s.prizeMin, "max-w-[60%]")
              : s.prize
        )}
      >
        {prize ?? "—"}
      </PrizeText>
    </div>
  );
};

/** A single place (no band) — the only one that gets a podium treatment. */
const isSinglePlace = (from?: number | null, to?: number | null) =>
  from != null && (to == null || to === from);

/**
 * The place cell of a prize or winner row: a podium medal for single places 1–3, then ONE ghost
 * badge — quiet, so the prize keeps the focus: a band "4 → 10" in the secondary text colour, a
 * podium place in its metal. The plain "4–10" stays for assistive tech; the badge is decoration.
 * Without a place (`from` null) it is a dash.
 */
export const PlaceBadge = ({
  from,
  to,
  size = "compact",
}: {
  from?: number | null;
  to?: number | null;
  size?: LeaderboardRowSize;
}) => {
  const s = SIZE[size];
  const single = isSinglePlace(from, to);
  const podium = single && from != null ? PODIUM[from] : undefined;
  const rankLabel =
    from == null ? "—" : single ? String(from) : `${from}–${to}`;
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {podium && (
        <Medal
          className={cn("shrink-0", s.medal, podium.icon)}
          strokeWidth={1.5}
        />
      )}
      {from != null ? (
        <span className="flex items-center">
          <span className="sr-only">{rankLabel}</span>
          <Badge
            aria-hidden
            variant="ghost"
            color={podium?.badge ?? "neutral"}
            className={RANGE_BADGE}
          >
            {single ? (
              String(from)
            ) : (
              <span className="inline-flex items-center gap-0.5">
                {String(from)}
                <ArrowRight className="size-2.5 shrink-0" strokeWidth={2.75} />
                {String(to)}
              </span>
            )}
          </Badge>
        </span>
      ) : (
        <span
          className={cn(
            "font-heading font-normal whitespace-nowrap text-muted-foreground",
            s.type
          )}
        >
          {rankLabel}
        </span>
      )}
    </div>
  );
};

/**
 * A prize-table row on the same board grid as the leaderboard rows (Info tab → "Prizes"): the
 * rank range where the score would be a name, the prize in the prize column — so the list of
 * what's on offer reads like the board it pays out on. Podium treatment for single ranks 1–3;
 * a range ("4–5", "6–10") is a quiet row. Free-spins games hang under the prize as chips.
 */

/** Same row, same data → no re-render. The board's items are rebuilt on every live push (every few
 *  seconds), so compare by value: only rows whose rank, score, name or prize actually changed
 *  render again — on a 1,000-row board that is a handful instead of all of them. */
const sameLeaderboardItem = (
  a: TournamentLeaderboardItem,
  b: TournamentLeaderboardItem
) =>
  a === b ||
  (a.id === b.id &&
    a.position === b.position &&
    a.score === b.score &&
    a.name === b.name &&
    a.isPlayer === b.isPlayer &&
    a.prize === b.prize &&
    a.isEmptySlot === b.isEmptySlot);

export const LeaderboardRow = memo(
  LeaderboardRowImpl,
  (prev, next) =>
    sameLeaderboardItem(prev.item, next.item) &&
    prev.scoreSuffix === next.scoreSuffix &&
    prev.isYou === next.isYou &&
    prev.size === next.size &&
    prev.prizeFit === next.prizeFit &&
    prev.layout === next.layout &&
    prev.nameVisible === next.nameVisible &&
    prev.className === next.className &&
    prev.ref === next.ref
);

/** The place badge ("1", "4 → 10"): pulled in tight so it reads as one small token in the
 *  rank column. Overrides the `sm` size at both breakpoints. */
const RANGE_BADGE =
  "px-1.5 py-0 text-[11px] leading-[18px] tabular-nums lg:px-1.5 lg:py-0 lg:text-[12px] lg:leading-5";

export const LeaderboardPrizeRow = ({
  from,
  to,
  prize,
  games = [],
  size = "compact",
  className,
}: {
  from?: number | null;
  to?: number | null;
  prize: string;
  games?: string[];
  size?: LeaderboardRowSize;
  className?: string;
}) => {
  const s = SIZE[size];
  const podium =
    isSinglePlace(from, to) && from != null ? PODIUM[from] : undefined;
  return (
    <div
      // Lets the prize board stripe the plain rows only (`.prize-board`, index.css).
      data-podium={podium ? from : undefined}
      className={cn(
        "col-span-full grid grid-cols-subgrid items-center",
        s.gap,
        s.pad,
        podium ? podium.bg : "bg-background-card-surface",
        className
      )}
    >
      <PlaceBadge from={from} to={to} size={size} />
      {/* The name column, empty: it is the space that pushes the prize to the right. */}
      <span aria-hidden className="min-w-0" />
      {/* Score, dot and prize columns, taken as one so the prize can wrap whole. */}
      <div className="col-span-3 flex min-w-0 flex-col items-end gap-1.5">
        <PrizeText
          title={prize}
          size="inherit"
          weight={podium ? "semibold" : "medium"}
          tone={podium ? "gold" : "flat"}
          shadow={false}
          // One line that glides when too long — the gold gradient is painted per line, so a
          // wrapped title read as two different prizes. Same fit as the leaderboard rows.
          fit="marquee"
          className={cn(
            "max-w-full min-w-0 text-right font-heading",
            s.prizeType,
            !podium && "text-prizes/75"
          )}
        >
          {prize}
        </PrizeText>
      </div>
      {/* The prize's games on a line of their own, from the (empty) name column to the end: the
          names get that free width to truncate in, and the prize column stays as wide as the
          prize. `-mt-1` pulls it toward the prize it belongs to. */}
      {games.length > 0 && (
        <PrizeGamesChips
          games={games}
          className="col-start-2 col-end-[-1] -mt-1 justify-end"
        />
      )}
    </div>
  );
};

/**
 * Greyed placeholder row for an empty leaderboard (no participants yet). Mirrors
 * the row's columns so it reads as an empty leaderboard slot rather than a void.
 */
export const LeaderboardEmptyRow = ({
  rank,
  prize,
  size = "default",
  layout = "flex",
  nameVisible = true,
  className,
}: {
  rank: number;
  nameVisible?: boolean;
  /** Prize for this rank — highlighted in gold so the empty board still
   *  previews what's on offer at each position. */
  prize?: string;
  size?: LeaderboardRowSize;
  layout?: "flex" | "grid";
  className?: string;
}) => {
  const grid = layout === "grid";
  const s = SIZE[size];
  return (
    <div
      role="row"
      className={cn(
        grid
          ? "col-span-full grid grid-cols-subgrid items-center"
          : "flex w-full items-center",
        "bg-background-card-surface/50",
        s.gap,
        s.pad,
        className
      )}
    >
      <span
        role="cell"
        className={cn(
          "shrink-0 font-heading text-muted-foreground/60",
          s.type,
          !grid && s.rank
        )}
      >
        {rank}
      </span>
      <span
        role="cell"
        className={cn(
          "min-w-0 flex-1 font-heading text-muted-foreground/50",
          s.type
        )}
      >
        {nameVisible && "—"}
      </span>
      <span
        role="cell"
        className={cn(
          "shrink-0 text-right font-heading text-muted-foreground/50",
          s.type,
          !grid && s.score
        )}
      >
        —
      </span>
      <span
        className={cn("shrink-0 text-center text-border/60", !grid && s.dot)}
        aria-hidden
      >
        ·
      </span>
      <PrizeText
        role="cell"
        size="inherit"
        weight="medium"
        tone={prize ? "flat" : "muted"}
        shadow={false}
        fit="marquee"
        className={cn(
          // Prize-priority: grow to fit the full prize (capped at 60%), then shrink
          // and glide — never truncate. Matches the data rows.
          "shrink-0 text-right font-heading",
          s.type,
          !grid && cn(s.prizeMin, "max-w-[60%]"),
          !prize && "text-muted-foreground/50"
        )}
      >
        {prize ?? "—"}
      </PrizeText>
    </div>
  );
};

/** Pill that scrolls the leaderboard to "Top" / "Around me". Shared by the
 *  desktop table's scroll bar and the mobile dialog's filters. */
export const LeaderboardFilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex cursor-pointer items-center justify-center rounded-full px-3 py-1.5 font-heading text-[12px] leading-4 font-semibold transition-colors",
      active
        ? "bg-primary text-background"
        : "bg-background-card-surface text-muted-foreground hover:text-foreground"
    )}
  >
    {children}
  </button>
);

/**
 * Desktop leaderboard for the campaign shell: a fixed-height, inline-scrollable
 * list of the whole leaderboard (~10 rows tall) with a pinned "You" row that
 * appears when the player's own row is scrolled out of view — under the header
 * once scrolled past it, at the bottom while it's still below — plus a "scroll to"
 * bar (Top / Around me) beneath it. Figma 5129:6079 / 4992:29641. (Mobile uses
 * TournamentBoard, the board inline in the Overview, instead.)
 */
export const TournamentLeaderboardTable = ({
  campaignId,
  className,
}: {
  campaignId?: string | number;
  className?: string;
}) => {
  const { t } = useTranslation();
  const id = campaignId != null ? String(campaignId) : undefined;
  const {
    fullLeaderboardData,
    playerData,
    scoreLabel,
    showScoreMultiplierSuffix,
    prizeForPosition,
    loading,
  } = useTournamentLeaderboard(id);
  const scoreSuffix = showScoreMultiplierSuffix ? "x" : "";
  const reduceMotion = useReducedMotion();

  const rows = fullLeaderboardData;
  const isEmpty = !loading && rows.length === 0;

  // Pinned "You" row: shown only when the player's row isn't on screen, on the
  // edge it left by — under the header once scrolled past it, at the bottom while
  // it's still below the view (or not in the list yet). Measured on scroll, not
  // with an IntersectionObserver: that only fires on a threshold crossing, so a
  // fast scroll that jumps clean over the row would leave the pin on the old edge.
  const listRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const playerRowRef = useRef<HTMLDivElement>(null);
  const [playerOffscreen, setPlayerOffscreen] = useState<
    "top" | "bottom" | null
  >(null);

  useEffect(() => {
    const root = listRef.current;
    if (!root) return;
    const update = () => {
      const target = playerRowRef.current;
      if (!target) return setPlayerOffscreen(null);
      const row = target.getBoundingClientRect();
      const middle = row.top + row.height / 2;
      // The sticky header covers the top of the scroller, so a row under it is out of view.
      const top =
        headerRef.current?.getBoundingClientRect().bottom ??
        root.getBoundingClientRect().top;
      const bottom = root.getBoundingClientRect().bottom;
      setPlayerOffscreen(
        middle < top ? "top" : middle > bottom ? "bottom" : null
      );
    };
    update();
    root.addEventListener("scroll", update, { passive: true });
    return () => root.removeEventListener("scroll", update);
  }, [rows]);

  const playerInList = rows.some((r) => r.isPlayer);
  const pinnedYou = !playerData
    ? null
    : !playerInList
      ? "bottom"
      : playerOffscreen;
  const renderPinnedYou = (edge: "top" | "bottom") => (
    <AnimatePresence initial={false}>
      {pinnedYou === edge && playerData && (
        <motion.div
          key={`you-${edge}`}
          initial={
            reduceMotion ? false : { opacity: 0, y: edge === "top" ? -12 : 12 }
          }
          animate={{ opacity: 1, y: 0 }}
          exit={
            reduceMotion
              ? { opacity: 0 }
              : { opacity: 0, y: edge === "top" ? -12 : 12 }
          }
          transition={{ duration: 0.2 }}
          className={cn(
            "col-span-full grid grid-cols-subgrid bg-background-card",
            edge === "top" ? "self-start" : "sticky bottom-0 z-10"
          )}
        >
          <LeaderboardRow
            item={playerData}
            scoreSuffix={scoreSuffix}
            layout="grid"
            isYou
            className={cn(edge === "bottom" && "rounded-b-(--border-radius)")}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const [scrollTarget, setScrollTarget] = useState<"top" | "around">("top");
  const scrollBehavior: ScrollBehavior = reduceMotion ? "auto" : "smooth";
  const scrollToTop = () => {
    setScrollTarget("top");
    listRef.current?.scrollTo({ top: 0, behavior: scrollBehavior });
  };
  const scrollToPlayer = () => {
    setScrollTarget("around");
    playerRowRef.current?.scrollIntoView({
      block: "center",
      behavior: scrollBehavior,
    });
  };

  return (
    <div className={cn("flex w-full flex-col gap-3", className)}>
      <Card inset="none" className="relative flex flex-col">
        {/* One grid for the header, every row and the pinned "You" row, so the columns are
            shared: the header sits over its column and the prize column is as wide as the
            widest prize on the board — a real table, not rows that each size themselves. The
            header is sticky inside the scroller (a subgrid cannot reach across it). */}
        <div
          ref={listRef}
          role="table"
          aria-label={t("tournament.leaderboard", "Leaderboard")}
          className={cn(
            LEADERBOARD_GRID,
            "max-h-[446px] overflow-y-auto overscroll-y-contain"
          )}
        >
          {/* Header and the top-pinned "You" row stick as one block, so the row sits right
              under the header instead of both fighting for `top-0`. The row lives in a 0px
              track and overflows it: it overlays the list rather than pushing it down, which
              would shift the player's own row back into view and flicker the pin. */}
          <div
            role="rowgroup"
            className="sticky top-0 z-10 col-span-full grid grid-cols-subgrid grid-rows-[auto_0]"
          >
            <div
              ref={headerRef}
              role="row"
              className="col-span-full grid grid-cols-subgrid items-center gap-2.5 bg-background-card px-4 py-2.5"
            >
              {/* Rank header is "#", not a word: a translated label (e.g. Norwegian
                  "Plassering") is far wider than the number cell and would widen the whole
                  rank column. */}
              <SectionLabel role="columnheader">#</SectionLabel>
              <SectionLabel role="columnheader" className="min-w-0 truncate">
                {t("tournament.player", "Player")}
              </SectionLabel>
              <SectionLabel role="columnheader" className="text-right">
                {scoreLabel}
              </SectionLabel>
              <span aria-hidden />
              <SectionLabel role="columnheader" className="text-right">
                {t("tournament.prize", "Prize")}
              </SectionLabel>
            </div>
            {renderPinnedYou("top")}
          </div>

          {loading ? (
            <p className="col-span-full py-8 text-center font-body text-sm text-muted-foreground">
              {t("tournament.loadingLeaderboard", "Loading leaderboard…")}
            </p>
          ) : isEmpty ? (
            Array.from({ length: EMPTY_ROW_COUNT }, (_, index) => (
              <LeaderboardEmptyRow
                key={`empty-${index}`}
                rank={index + 1}
                prize={prizeForPosition(index + 1)}
                layout="grid"
                className={cn(index > 0 && "border-t border-border")}
              />
            ))
          ) : (
            rows.map((item, index) => (
              <LeaderboardRow
                // Key by the player hash (stable identity), not position — a live
                // re-rank then moves the row instead of reconciling the "you"
                // highlight onto whoever now sits at that rank (F3).
                key={item.id ?? `pos-${item.position}`}
                ref={item.isPlayer ? playerRowRef : undefined}
                item={item}
                scoreSuffix={scoreSuffix}
                layout="grid"
                // Hairlines are row borders (not separator rows) so every grid child is a
                // row and the subgrid tracks stay one-to-one with the board.
                className={cn(index > 0 && "border-t border-border")}
              />
            ))
          )}

          {/* Bottom-pinned "You" row — while the player's own row is still below the view.
              A sticky grid row, so it shares the columns too. */}
          {renderPinnedYou("bottom")}
        </div>
      </Card>

      {/* Scroll shortcuts — jump the list to the top or to the player's row.
          Hidden on this branch (tournament-layout only); drop `hidden` to restore. */}
      {!loading && !isEmpty && (
        <div className="hidden w-full items-center gap-2 px-1">
          <SectionLabel className="shrink-0">
            {t("tournament.scrollTo", "Scroll to")}
          </SectionLabel>
          <LeaderboardFilterChip
            active={scrollTarget === "top"}
            onClick={scrollToTop}
          >
            {t("tournament.filterTop", "Top")}
          </LeaderboardFilterChip>
          {playerData && (
            <LeaderboardFilterChip
              active={scrollTarget === "around"}
              onClick={scrollToPlayer}
            >
              {t("tournament.filterAroundMe", "Around me")}
            </LeaderboardFilterChip>
          )}
        </div>
      )}
    </div>
  );
};

export default TournamentLeaderboardTable;
